import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../services/supabase/supabase-config';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Alert from '../components/ui/Alert';

const SignUpPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [company, setCompany] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const formatPhone = (value) => {
    return value
      .replace(/\D/g, '')
      .replace(/^(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{5})(\d{4})$/, '$1-$2');
  };

  const handlePhoneChange = (e) => {
    const formattedPhone = formatPhone(e.target.value);
    setPhone(formattedPhone);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSuccessMessage('');

    if (!email || !password || !confirmPassword || !fullName || !phone || !company) {
      setError('Por favor, preencha todos os campos.');
      return;
    }
    if (password !== confirmPassword) {
      setError('As senhas não coincidem.');
      return;
    }
    if (password.length < 6) {
      setError('A senha deve ter pelo menos 6 caracteres.');
      return;
    }

    setLoading(true);

    try {
      const { error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          // Sem isto, o link do e-mail redireciona para o "Site URL" configurado
          // no painel do Supabase — que num projeto novo nasce como
          // http://localhost:3000 e leva o cliente para lugar nenhum.
          //
          // Usa window.location.origin para funcionar igual no site publicado e
          // no ambiente local. O destino precisa estar na lista de Redirect URLs
          // em Authentication -> URL Configuration, senao o Supabase ignora e cai
          // no Site URL de novo.
          emailRedirectTo: `${window.location.origin}/dashboard`,
          data: {
            full_name: fullName,
            company_name: company,
            phone: phone,
          },
        },
      });

      if (authError) {
        throw authError;
      }

      setSuccessMessage('Conta criada com sucesso! Verifique seu e-mail para confirmar.');

      // Preserva o contexto do plugin: se o cadastro veio de la, o login depois
      // precisa saber disso para devolver os tokens ao listener local em vez de
      // ir para o dashboard.
      const from = new URLSearchParams(window.location.search).get('from');
      const destinoLogin = from === 'plugin' ? '/login?from=plugin' : '/login';

      setTimeout(() => {
        navigate(destinoLogin);
      }, 3000);

    } catch (error) {
      setError(`Erro no cadastro: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const inputStyles = "mt-1 block w-full px-3 py-2 border border-rn-line rounded-md shadow-sm placeholder-rn-text-soft focus:outline-none focus:ring-2 focus:ring-rn-orange-text focus:border-rn-orange-text";

  return (
    <div className="flex items-center justify-center min-h-screen bg-rn-bg px-4 py-12">
      <Card className="w-full max-w-md p-8" hover={false}>
        <h2 className="text-2xl font-semibold text-center mb-6 text-rn-navy">Criar Conta</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="fullName" className="block text-sm font-medium text-rn-navy">Nome Completo</label>
            <input type="text" id="fullName" value={fullName} onChange={(e) => setFullName(e.target.value)} disabled={loading} required className={inputStyles} />
          </div>
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-rn-navy">Email</label>
            <input type="email" id="email" value={email} onChange={(e) => setEmail(e.target.value)} disabled={loading} required className={inputStyles} />
          </div>
          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-rn-navy">Telefone</label>
            <input type="tel" id="phone" value={phone} onChange={handlePhoneChange} disabled={loading} required maxLength="15" className={inputStyles} />
          </div>
          <div>
            <label htmlFor="company" className="block text-sm font-medium text-rn-navy">Empresa</label>
            <input type="text" id="company" value={company} onChange={(e) => setCompany(e.target.value)} disabled={loading} required className={inputStyles} />
          </div>
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-rn-navy">Senha</label>
            <input type="password" id="password" value={password} onChange={(e) => setPassword(e.target.value)} disabled={loading} required className={inputStyles} />
          </div>
          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-rn-navy">Confirmar Senha</label>
            <input type="password" id="confirmPassword" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} disabled={loading} required className={inputStyles} />
          </div>

          {error && <Alert type="error" message={error} />}
          {successMessage && <Alert type="success" message={successMessage} />}

          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? 'Criando Conta...' : 'Criar Conta'}
          </Button>
        </form>
        <div className="mt-6 text-center text-sm">
          <p className="text-rn-text-soft">
            Já tem uma conta?{' '}
            <Link to="/login" className="font-medium text-rn-orange-text hover:text-rn-orange">
              Faça login
            </Link>
          </p>
        </div>
      </Card>
    </div>
  );
};

export default SignUpPage;
